import { StanModal } from '../core-components/StanModal/StanModal';

interface NavigationConfirmationModalProps {
    /**
     * Whether the modal is open
     */
    isOpen: boolean;
    /**
     * Callback when user chooses to stay on the page (keeps unsaved changes)
     */
    onStay: () => void;
    /**
     * Callback when user chooses to discard changes and navigate away
     */
    onDiscard: () => void;
    /**
     * Custom title for the modal (default: "Unsaved Changes")
     */
    title?: string;
    /**
     * Custom message for the modal (default: "You have unsaved changes. What would you like to do?")
     */
    message?: string;
    /**
     * Custom label for the stay button (default: "Stay on Page")
     */
    stayLabel?: string;
    /**
     * Custom label for the discard button (default: "Discard Changes")
     */
    discardLabel?: string;
}

/**
 * Reusable modal for confirming navigation when there are unsaved changes.
 * Integrates with the global navigation store via useNavigationStore.
 *
 * @example
 * ```tsx
 * // Global usage in App.tsx
 * const { showConfirmation, cancelNavigation, proceedWithNavigation } = useNavigationStore();
 *
 * return (
 *   <>
 *     <Routes>...</Routes>
 *     <NavigationConfirmationModal
 *       isOpen={showConfirmation}
 *       onStay={cancelNavigation}
 *       onDiscard={proceedWithNavigation}
 *     />
 *   </>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Custom usage with different labels
 * <NavigationConfirmationModal
 *   isOpen={showConfirmation}
 *   onStay={cancelNavigation}
 *   onDiscard={proceedWithNavigation}
 *   title="Unsaved Email"
 *   message="Your email has unsaved changes."
 *   stayLabel="Keep Editing"
 *   discardLabel="Discard Email"
 * />
 * ```
 */
export const NavigationConfirmationModal = ({
    isOpen,
    onStay,
    onDiscard,
    title = 'Heads up!',
    message = 'You have unsaved changes. What would you like to do?',
    stayLabel = 'Stay on Page',
    discardLabel = 'Discard Changes',
}: NavigationConfirmationModalProps) => {
    return (
        <StanModal
            id='navigation-confirmation-modal'
            isOpen={isOpen}
            onClose={onStay}
            size='sm'
            title={title}
            className='pt-4'
            primaryActionLabel={stayLabel}
            onPrimaryAction={onStay}
            secondaryActionLabel={discardLabel}
            onSecondaryAction={onDiscard}
        >
            <p className='text-gray text-label-sm-regular -mt-2 pb-2 text-center'>{message}</p>
        </StanModal>
    );
};
